import React, { useContext, useEffect, useMemo, useState } from 'react';
import SEO from '../../components/SEO/SEO';
import { ClerkLoaded, ClerkLoading, SignUp } from "@clerk/clerk-react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import style from '../Login/Login.module.css';
import spinner from '../../assets/Spinner.svg';
import { useAuthUser } from '../../hooks/useAuthUser';
import { AppAuthContext } from '../../contexts/AppAuthContext';
import { requestWithApiFallback } from '../../utils/apiRequest';
import { buildAuthCallbackRedirectUrl, resolveAuthDestinationFromLocation } from '../../utils/authRedirect';

const Signup = () => {
	const API_URL = process.env.REACT_APP_API_URL;
	const { user } = useAuthUser();
	const { isTestAuthEnabled, setTestAuthSession } = useContext(AppAuthContext);
	const navigate = useNavigate();
	const location = useLocation();
	const [username, setUsername] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [forceHostedAuth, setForceHostedAuth] = useState(false);

	const finalDestination = useMemo(() => {
		return resolveAuthDestinationFromLocation(location, '/choice');
	}, [location]);

	const callbackRedirectUrl = useMemo(
		() => buildAuthCallbackRedirectUrl(finalDestination),
		[finalDestination]
	);

	const isForcedTestRoute = useMemo(
		() => location.pathname === '/test-signup' || location.pathname === '/test-sign-up',
		[location.pathname]
	);
	const useTestAuthUI = (isTestAuthEnabled || isForcedTestRoute) && !forceHostedAuth;
	const loginLink = useMemo(
		() => `${useTestAuthUI ? '/test-signin' : '/login'}?redirectTo=${encodeURIComponent(finalDestination)}`,
		[finalDestination, useTestAuthUI]
	);

	useEffect(() => {
		if (user) {
			navigate(finalDestination, { replace: true });
		}
	}, [user, navigate, finalDestination]);

	const handleTestSignUp = async (event) => {
		event.preventDefault();
		setErrorMessage('');

		if (!username.trim() || !email.trim() || !password) {
			setErrorMessage('Username, email, and password are required.');
			return;
		}
		if (password.length < 8) {
			setErrorMessage('Password must be at least 8 characters.');
			return;
		}
		if (password !== confirmPassword) {
			setErrorMessage('Passwords do not match.');
			return;
		}

		setIsSubmitting(true);
		try {
			const response = await requestWithApiFallback(API_URL, (base) => axios.post(
				`${base}/test-signup`,
				{
					username: username.trim(),
					email: email.trim(),
					password
				}
			));

			setTestAuthSession({
				token: response.data.token,
				user: response.data.user,
				signedInAt: new Date().toISOString()
			});
			navigate(finalDestination, { replace: true });
		} catch (error) {
			if (error?.response?.status === 404) {
				setForceHostedAuth(true);
				setErrorMessage('');
				return;
			}
			setErrorMessage(error?.response?.data?.error || 'Could not create account right now.');
		} finally {
			setIsSubmitting(false);
		}
	};

	if (useTestAuthUI) {
		return (
			<div className={style.LoginPage}>
				<SEO title="Sign up" description="Join Calcium to create, track, and share your fitness routines." path="/signup" noIndex />
				<div className={style.AuthShell}>
					<div className={style.AuthHeader}>
						<h1 className={style.AuthTitle}>Create Account</h1>
						<p className={style.AuthSubtitle}>Start your Calcium journey in under a minute.</p>
					</div>

					<form className={style.TestAuthForm} onSubmit={handleTestSignUp}>
						<label className={style.AuthLabel} htmlFor='test-auth-username'>
							Username
						</label>
						<input
							id='test-auth-username'
							className={style.AuthInput}
							type='text'
							value={username}
							onChange={(event) => setUsername(event.target.value)}
							placeholder='Choose a username'
							autoComplete='username'
						/>

						<label className={style.AuthLabel} htmlFor='test-auth-email-signup'>
							Email
						</label>
						<input
							id='test-auth-email-signup'
							className={style.AuthInput}
							type='email'
							value={email}
							onChange={(event) => setEmail(event.target.value)}
							placeholder='you@example.com'
							autoComplete='email'
						/>

						<label className={style.AuthLabel} htmlFor='test-auth-password-signup'>
							Password
						</label>
						<input
							id='test-auth-password-signup'
							className={style.AuthInput}
							type='password'
							value={password}
							onChange={(event) => setPassword(event.target.value)}
							placeholder='At least 8 characters'
							autoComplete='new-password'
						/>

						<label className={style.AuthLabel} htmlFor='test-auth-confirm-password'>
							Confirm Password
						</label>
						<input
							id='test-auth-confirm-password'
							className={style.AuthInput}
							type='password'
							value={confirmPassword}
							onChange={(event) => setConfirmPassword(event.target.value)}
							placeholder='Re-enter password'
							autoComplete='new-password'
						/>

						{errorMessage ? <p className={style.AuthError}>{errorMessage}</p> : null}

						<button className={style.AuthPrimaryButton} type='submit' disabled={isSubmitting}>
							{isSubmitting ? 'Creating account...' : 'Create account'}
						</button>
					</form>

					<p className={style.AuthFooterText}>
						Already have an account? <Link to={loginLink} className={style.AuthFooterLink}>Sign in</Link>
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className={style.LoginPage}>
			<SEO title="Sign up" description="Join Calcium to create, track, and share your fitness routines." path="/signup" noIndex />
			<div className={style.LoginForm}>
				<ClerkLoading>
					<img src={spinner} alt="Loading authentication..." />
				</ClerkLoading>
				<ClerkLoaded>
					{/* See Login for redirectUrl notes */}
					<SignUp
						redirectUrl={callbackRedirectUrl}
						afterSignUpUrl={finalDestination}
					/>
				</ClerkLoaded>
				{/* <SignUp redirectUrl={'http://localhost:3000/'} /> */}
			</div>
		</div>
	)
}

export default Signup;